import type { OdinKey } from '@myosh/odin-components';
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CombinedFieldType,
  DynamicFormFieldGroup,
  DynamicFormSettings,
  ExtendedDynamicFormField,
} from '../../@types/forms';
import type { FormHierarchyTypes } from '../../@types/hierarchy-fields';
import type { RootState } from '../store';

export interface FormBuilderFormSettings extends Omit<DynamicFormSettings, 'id' | 'fieldGroups'> {
  id?: number; // Making id optional
  fieldGroups: Array<FormBuilderFormGroup>;
}

export interface FormBuilderFormGroup extends Omit<DynamicFormFieldGroup, 'id' | 'fields'> {
  id?: number; // Making id optional
  fields: Array<FormBuilderField>;
  removed: boolean;
  uuid?: string; // Temporary ID for new groups
}
export interface FormBuilderField extends Omit<ExtendedDynamicFormField, 'id' | 'fieldType'> {
  id?: number; // Making id optional
  uuid?: string;
  groupId?: number | string; // ID of the group this field belongs to,
  fieldType: CombinedFieldType;
}

interface AISpecificConfig {
  singleRecordAiAnalysisPrompt?: string;
  bulkRecordAiAnalysisPrompt?: string;
}

interface DropFormBuilderConfig {
  droppedItem: OdinKey;
  overItem: OdinKey;
}

interface DropFormBuilderFieldConfig extends DropFormBuilderConfig {
  groupId: OdinKey;
}
interface MoveFormFieldBetweenGroupsConfig {
  fieldId: OdinKey;
  sourceGroupId: OdinKey;
  targetGroupId: OdinKey;
  overFieldId?: OdinKey;
}

export interface FormBuilerHierarchyType extends FormHierarchyTypes {
  visible: boolean;
}

type DeleteItemType = 'FIELD' | 'GROUP';

interface DeleteGroupOrFieldPayload {
  type: DeleteItemType;
  id: OdinKey;
}

interface FormBuilderState {
  activeMenuItem: string;
  isFormLoading: boolean;
  formData?: FormBuilderFormSettings;
  selectedField?: FormBuilderField;
  selectedHierarchy?: FormBuilerHierarchyType;
  selectedGroupId?: number | string;
  isDirty?: boolean;
  hasDeletedItems?: boolean;
  aiSpecificConfig?: AISpecificConfig;
}

const initialState: FormBuilderState = {
  activeMenuItem: 'Fields',
  isFormLoading: false,
  isDirty: false,
};

const formBuilderSlice = createSlice({
  name: 'formBuilder',
  initialState,
  reducers: {
    setActiveMenuItem: (state, action: PayloadAction<string>) => {
      state.activeMenuItem = action.payload;
    },
    setFormData: (state, action: PayloadAction<FormBuilderFormSettings>) => {
      state.formData = action.payload;
      state.isDirty = false;
    },
    setImportedFormData: (state, action: PayloadAction<FormBuilderFormSettings>) => {
      state.formData = action.payload;
      state.isDirty = true;
    },
    addFormField: (state, action: PayloadAction<{ groupId: number | string; field: FormBuilderField }>) => {
      if (!state.formData) return;

      const group = state.formData.fieldGroups.find(
        (group) => group.id === action.payload.groupId || group.uuid === action.payload.groupId
      );
      if (group) {
        if (!group.fields) {
          group.fields = [];
        }
        group.fields.push(action.payload.field);

        state.selectedField = action.payload.field;
        const fieldId = action.payload.field.id ?? action.payload.field.uuid;
        if (fieldId) {
          state.isDirty = true;
        }

        state.selectedGroupId = undefined;
      }
    },
    updateHierarchyFieldData: (state, action: PayloadAction<FormBuilerHierarchyType>) => {
      if (!state.formData || !action.payload.id) return;

      const { payload } = action;

      if (!state.formData.hierarchyTypes) {
        if (payload.visible) {
          state.formData.hierarchyTypes = [payload];
          state.isDirty = true;
        }
        return;
      }

      const hierarchyType = state.formData.hierarchyTypes.find((field) => field.id === payload.id);

      if (hierarchyType) {
        if (payload.visible === false) {
          state.formData.hierarchyTypes = state.formData.hierarchyTypes.filter((ht) => ht.id !== payload.id);
        } else {
          Object.assign(hierarchyType, payload);
        }
        state.isDirty = true;
      } else if (payload.visible === true) {
        state.formData.hierarchyTypes.push(payload);
        state.isDirty = true;
      }
    },
    updateFormFieldData: (state, action: PayloadAction<Partial<FormBuilderField>>) => {
      if (!state.formData) return;

      const group = state.formData.fieldGroups.find(
        (group) => group.id === action.payload.groupId || group.uuid === action.payload.groupId
      );
      if (group) {
        const field = group?.fields?.find((field) => {
          if (field.id && action.payload.id) {
            return field.id === action.payload.id;
          } else if (field.uuid && action.payload.uuid) {
            return field.uuid === action.payload.uuid;
          }
        });

        if (field) {
          Object.assign(field, action.payload);
          Object.assign(state.selectedField!, action.payload);
          state.isDirty = true;
        }
      }
    },
    addFormGroup: (state, action: PayloadAction<FormBuilderFormGroup>) => {
      if (!state.formData) return;

      state.formData.fieldGroups.push(action.payload);
      if (action.payload.uuid) {
        state.isDirty = true;
      }
      state.selectedGroupId = action.payload.id ?? action.payload.uuid;
      state.selectedField = undefined;
    },
    updateFormGroup: (state, action: PayloadAction<Partial<FormBuilderFormGroup>>) => {
      if (!state.formData) return;

      for (let i = 0; i < state.formData.fieldGroups.length; i++) {
        if (
          (action.payload.id && state.formData.fieldGroups[i].id === action.payload.id) ||
          (action.payload.uuid && state.formData.fieldGroups[i].uuid === action.payload.uuid)
        ) {
          Object.assign(state.formData.fieldGroups[i], action.payload);
          state.isDirty = true;
          break;
        }
      }
    },
    setSelectedField: (state, action: PayloadAction<FormBuilderField | undefined>) => {
      state.selectedField = action.payload;
      state.selectedHierarchy = undefined;
      state.selectedGroupId = undefined;
    },
    setSelectedGroupId: (state, action: PayloadAction<number | string | undefined>) => {
      state.selectedGroupId = action.payload;
      state.selectedField = undefined;
      state.selectedHierarchy = undefined;
    },
    setSelectedHierarchy: (state, action: PayloadAction<FormBuilerHierarchyType | undefined>) => {
      if (!action.payload) {
        state.selectedHierarchy = undefined;
      } else {
        const formHierarchy = state.formData?.hierarchyTypes?.find((ht) => ht.id === action.payload?.id);
        if (formHierarchy) {
          state.selectedHierarchy = { ...formHierarchy, visible: action.payload.visible };
        }
      }
      state.selectedGroupId = undefined;
      state.selectedField = undefined;
    },
    setAISpecificConfig: (state, action: PayloadAction<AISpecificConfig>) => {
      if (!state.aiSpecificConfig) {
        state.aiSpecificConfig = {};
      }
      state.aiSpecificConfig = {
        ...state.aiSpecificConfig,
        ...action.payload,
      };
    },
    resetAiSpecificConfig: (state) => {
      state.aiSpecificConfig = {};
    },
    setFormLoadingState: (state, action: PayloadAction<boolean>) => {
      state.isFormLoading = action.payload;
    },
    setFormIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    reorderFormGroups: (state, action: PayloadAction<DropFormBuilderConfig>) => {
      if (!state.formData?.fieldGroups) return;

      const { droppedItem, overItem } = action.payload;
      let sourceIndex = -1;
      let targetIndex = -1;
      // Find original indexes of the dragged section and section with was dropped over
      for (let i = 0; i < state.formData.fieldGroups.length; i++) {
        const group = state.formData.fieldGroups[i];
        const groupId = group.id ?? group.uuid;
        if (groupId === droppedItem) {
          sourceIndex = i;
        }
        if (groupId === overItem) {
          targetIndex = i;
        }
        // we have found both items, no need to continue looping
        if (sourceIndex !== -1 && targetIndex !== -1) {
          break;
        }
      }

      if (sourceIndex !== -1 && targetIndex !== -1 && sourceIndex !== targetIndex) {
        const reorderedGroups = [...state.formData.fieldGroups];
        // remove dragged item
        const [movedGroup] = reorderedGroups.splice(sourceIndex, 1);
        // insert dragged item at the position of the item it was dropped over
        reorderedGroups.splice(targetIndex, 0, movedGroup);

        // Update positions to reflect new order
        reorderedGroups.forEach((group, index) => {
          group.position = index + 1;
        });

        state.formData.fieldGroups = reorderedGroups;
        state.isDirty = true;
      }
    },
    reorderFormFields: (state, action: PayloadAction<DropFormBuilderFieldConfig>) => {
      if (!state.formData?.fieldGroups) return;

      const { groupId, droppedItem, overItem } = action.payload;
      const group = state.formData.fieldGroups.find((group) => (group.id ?? group.uuid) === groupId);

      if (!group?.fields) return;

      let sourceIndex = -1;
      let targetIndex = -1;

      for (let i = 0; i < group.fields.length; i++) {
        const field = group.fields[i];
        const fieldId = field.id ?? field.uuid;
        if (fieldId === droppedItem) {
          sourceIndex = i;
        }
        if (fieldId === overItem) {
          targetIndex = i;
        }
        if (sourceIndex !== -1 && targetIndex !== -1) {
          break;
        }
      }

      if (sourceIndex !== -1 && targetIndex !== -1 && sourceIndex !== targetIndex) {
        const reorderedFields = [...group.fields];
        const [movedField] = reorderedFields.splice(sourceIndex, 1);
        reorderedFields.splice(targetIndex, 0, movedField);

        // Update positions to reflect new order
        reorderedFields.forEach((field, index) => {
          field.position = index + 1;
        });

        group.fields = reorderedFields;
        state.isDirty = true;
      }
    },
    moveFormFieldBetweenGroups: (state, action: PayloadAction<MoveFormFieldBetweenGroupsConfig>) => {
      if (!state.formData?.fieldGroups) return;

      const { fieldId, sourceGroupId, targetGroupId, overFieldId } = action.payload;

      let fromGroup, toGroup;
      for (const group of state.formData.fieldGroups) {
        const groupId = group.id ?? group.uuid;
        if (groupId === sourceGroupId) {
          fromGroup = group;
        }
        if (groupId === targetGroupId) {
          toGroup = group;
        }
        if (fromGroup && toGroup) {
          break;
        }
      }

      if (!fromGroup?.fields || !toGroup) {
        return;
      }

      // Find and remove field from source
      const fieldIndex = fromGroup.fields.findIndex((f) => (f.id ?? f.uuid) === fieldId);
      if (fieldIndex === -1) {
        return;
      }

      const [movedField] = fromGroup.fields.splice(fieldIndex, 1);
      movedField.groupId = targetGroupId;

      // Initialize target fields array if needed
      if (!toGroup.fields) toGroup.fields = [];

      // Insert at position
      const targetIndex = overFieldId ? toGroup.fields.findIndex((f) => (f.id ?? f.uuid) === overFieldId) : -1;

      if (targetIndex !== -1) {
        toGroup.fields.splice(targetIndex, 0, movedField);
      } else {
        toGroup.fields.push(movedField);
      }

      // Update positions
      for (let i = 0; i < fromGroup.fields.length; i++) {
        fromGroup.fields[i].position = i + 1;
      }
      for (let i = 0; i < toGroup.fields.length; i++) {
        toGroup.fields[i].position = i + 1;
      }

      state.isDirty = true;
    },
    deleteGroupOrField: (state, action: PayloadAction<DeleteGroupOrFieldPayload>) => {
      if (!state.formData) return;
      const { id, type } = action.payload;

      if (!id) return;

      if (type === 'FIELD') {
        const fields = state.formData.fieldGroups.flatMap((group) => group.fields ?? []);
        const field = fields.find((f) => f?.id === id || f?.uuid === id);
        if (field) {
          field.removed = true;
          state.isDirty = true;
          state.hasDeletedItems = true;
          const selectedFieldId = state.selectedField?.id ?? state.selectedField?.uuid;
          if (selectedFieldId === id) {
            state.selectedField = undefined;
          }
        }
      } else if (type === 'GROUP') {
        const group = state.formData.fieldGroups.find((group) => group?.id === id || group?.uuid === id);
        if (group) {
          group.removed = true;
          state.isDirty = true;
          state.hasDeletedItems = true;
          if (state.selectedGroupId === id) {
            state.selectedGroupId = undefined;
          }
        }
      }
    },
    updateFormCaption: (state, action: PayloadAction<string>) => {
      if (!state.formData?.formCaption?.translations?.[0]) return;
      state.formData.formCaption.translations[0].value = action.payload;
      state.isDirty = true;
    },
    resetState: () => initialState,
  },
});

// actions
export const {
  setActiveMenuItem,
  setFormData,
  setImportedFormData,
  addFormField,
  updateHierarchyFieldData,
  updateFormFieldData,
  addFormGroup,
  setSelectedHierarchy,
  updateFormGroup,
  setSelectedField,
  setSelectedGroupId,
  setAISpecificConfig,
  resetAiSpecificConfig,
  setFormLoadingState,
  setFormIsDirty,
  reorderFormGroups,
  reorderFormFields,
  moveFormFieldBetweenGroups,
  deleteGroupOrField,
  updateFormCaption,
  resetState,
} = formBuilderSlice.actions;

// Shared empty array to preserve referential equality and prevent unnecessary rerenders when state data is undefined.
const EMPTY_ARRAY: [] = [];

// selectors
export const formBuilderState = (state: RootState) => state.formBuilder;
export const selectFormData = (state: RootState) => state.formBuilder.formData;
export const selectFormId = (state: RootState) => state.formBuilder.formData?.id;
export const selectFormCaption = (state: RootState) =>
  state.formBuilder.formData?.formCaption?.translations?.[0]?.value;
export const selectSelectedField = (state: RootState) => state.formBuilder.selectedField;
export const selectIsFieldDetailsOpen = (state: RootState) => !!state.formBuilder.selectedField;
export const selectIsDirty = (state: RootState) => state.formBuilder.isDirty;
export const selectSelectedFieldType = (state: RootState) => state.formBuilder.selectedField?.fieldType;
export const selectActiveMenuItem = (state: RootState) => state.formBuilder.activeMenuItem;
export const selectIsFormLoading = (state: RootState) => state.formBuilder.isFormLoading;
export const selectIsGroupDetailsOpen = (state: RootState) => !!state.formBuilder.selectedGroupId;
export const selectFormFieldGroupLength = (state: RootState) => state.formBuilder.formData?.fieldGroups?.length ?? 0;
export const selectSelectedHierarchy = (state: RootState) => state.formBuilder.selectedHierarchy;
export const selectIsHierarchyDetailsOpen = (state: RootState) => !!state.formBuilder.selectedHierarchy;
export const selectHierarchyEnableAdvance = (state: RootState) =>
  Boolean(state.formBuilder.formData?.hierarchyEnableAdvance);
export const selectAutoPopulateHierarchyFromPersonFieldId = (state: RootState) =>
  state.formBuilder.formData?.autoPopulateHierarchyFromPersonFieldId;
export const selectAutoPopulateHierarchyFromUserHierarchy = (state: RootState) =>
  state.formBuilder.formData?.autoPopulateHierarchyFromUserHierarchy;
export const selectHierarchyHiddenInReadMode = (state: RootState) =>
  Boolean(state.formBuilder.formData?.hierarchyHiddenInReadMode);
export const selectHierarchyDisplayType = (state: RootState) => state.formBuilder.formData?.hierarchyDisplayType;
export const selectBulkRecordAIAnalysisPrompt = (state: RootState) =>
  state.formBuilder?.aiSpecificConfig?.bulkRecordAiAnalysisPrompt;
export const selectSingleRecordAIAnalysisPrompt = (state: RootState) =>
  state.formBuilder?.aiSpecificConfig?.singleRecordAiAnalysisPrompt;
export const selectHasDeletedItems = (state: RootState) => state.formBuilder.hasDeletedItems ?? false;
export const selectHierarchyTypes = (state: RootState) => state.formBuilder.formData?.hierarchyTypes ?? EMPTY_ARRAY;

export const selectFieldGroups = createSelector(
  [(state: RootState) => state.formBuilder.formData?.fieldGroups],
  (fieldGroups) => fieldGroups?.filter((group) => !group.removed) ?? EMPTY_ARRAY
);

export const selectIsFieldSelected = createSelector(
  [selectSelectedField, (_, fieldId) => fieldId],
  (selectedField, fieldId) => (selectedField?.id ? selectedField?.id === fieldId : selectedField?.uuid === fieldId)
);

export const selectGroupById = createSelector([selectFieldGroups, (_, groupId) => groupId], (fieldGroups, groupId) =>
  fieldGroups.find((group) => (group.id ? group.id === groupId : group.uuid === groupId))
);

export const selectSelectedGroup = createSelector(
  [selectFieldGroups, (state: RootState) => state.formBuilder.selectedGroupId],
  (fieldGroups, selectedGroupId) =>
    fieldGroups.find((group) => (group.id ? group.id === selectedGroupId : group.uuid === selectedGroupId)) || {
      id: selectedGroupId,
    }
);

// reducer
export default formBuilderSlice.reducer;
